import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, TrendingUp, DollarSign, AlertTriangle, CreditCard, BarChart3, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export function AdminSubscriptionsTab({ subHealth }) {
  return (
    <div className="space-y-6" data-testid="subscriptions-health-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0" data-testid="sub-mrr-card">
          <CardContent className="pt-5 text-center">
            <DollarSign className="h-5 w-5 mx-auto mb-1 opacity-80" />
            <p className="text-3xl font-bold">R$ {subHealth.mrr?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
            <p className="text-xs opacity-80 mt-1">MRR (Receita Recorrente Mensal)</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0" data-testid="sub-active-card">
          <CardContent className="pt-5 text-center">
            <Users className="h-5 w-5 mx-auto mb-1 opacity-80" />
            <p className="text-3xl font-bold">{subHealth.total_active}</p>
            <p className="text-xs opacity-80 mt-1">Assinantes Ativos</p>
          </CardContent>
        </Card>
        <Card className={`bg-gradient-to-br border-0 text-white ${subHealth.churn_rate > 5 ? 'from-red-500 to-red-600' : 'from-slate-500 to-slate-600'}`} data-testid="sub-churn-card">
          <CardContent className="pt-5 text-center">
            <TrendingUp className="h-5 w-5 mx-auto mb-1 opacity-80" />
            <p className="text-3xl font-bold">{subHealth.churn_rate}%</p>
            <p className="text-xs opacity-80 mt-1">Taxa de Churn (30 dias)</p>
          </CardContent>
        </Card>
        <Card className={`bg-gradient-to-br border-0 text-white ${subHealth.past_due_count > 0 ? 'from-amber-500 to-amber-600' : 'from-slate-500 to-slate-600'}`} data-testid="sub-pastdue-card">
          <CardContent className="pt-5 text-center">
            <AlertTriangle className="h-5 w-5 mx-auto mb-1 opacity-80" />
            <p className="text-3xl font-bold">{subHealth.past_due_count}</p>
            <p className="text-xs opacity-80 mt-1">Pagamentos Pendentes</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center"><p className="text-2xl font-bold text-green-600">{subHealth.new_subscribers_30}</p><p className="text-xs text-slate-500 mt-1">Novas Assinaturas (30d)</p></CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center"><p className="text-2xl font-bold text-red-600">{subHealth.cancelled_last_30}</p><p className="text-xs text-slate-500 mt-1">Cancelamentos (30d)</p></CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center"><p className="text-2xl font-bold text-amber-600">{subHealth.cancelling_count}</p><p className="text-xs text-slate-500 mt-1">Cancelando (fim do periodo)</p></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border border-slate-200" data-testid="sub-plan-breakdown">
          <CardHeader><CardTitle className="text-lg text-slate-900 flex items-center gap-2"><CreditCard className="h-5 w-5" /> Assinantes por Plano</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {subHealth.plan_breakdown?.filter(p => p.plan !== 'gratuito').map(p => (
                <div key={p.plan} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div><p className="font-semibold capitalize text-slate-900">{p.label}</p><p className="text-xs text-slate-500">R$ {p.price?.toFixed(2)}/mes</p></div>
                  <div className="text-right"><p className="text-xl font-bold text-slate-900">{p.count}</p><p className="text-xs text-green-600 font-medium">R$ {p.mrr?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}/mes</p></div>
                </div>
              ))}
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-100 border border-slate-200">
                <div><p className="font-semibold text-slate-900">Gratuito</p><p className="text-xs text-slate-500">R$ 0,00/mes</p></div>
                <div className="text-right"><p className="text-xl font-bold text-slate-400">{subHealth.plan_breakdown?.find(p => p.plan === 'gratuito')?.count || 0}</p></div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200" data-testid="sub-evolution-chart">
          <CardHeader><CardTitle className="text-lg text-slate-900 flex items-center gap-2"><BarChart3 className="h-5 w-5" /> Evolucao Mensal</CardTitle></CardHeader>
          <CardContent>
            {subHealth.monthly_evolution?.length > 0 && (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={subHealth.monthly_evolution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }} />
                  <Bar dataKey="novas" fill="#22c55e" radius={[4, 4, 0, 0]} name="Novas" />
                  <Bar dataKey="renovacoes" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Renovacoes" />
                  <Bar dataKey="canceladas" fill="#ef4444" radius={[4, 4, 0, 0]} name="Canceladas" />
                  <Bar dataKey="falhas" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Falhas" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {subHealth.past_due_users?.length > 0 && (
        <Card className="bg-white border-2 border-amber-300" data-testid="sub-pastdue-alerts">
          <CardHeader><CardTitle className="text-lg text-amber-700 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Alertas de Pagamento</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-200 text-xs text-slate-500"><th className="text-left py-2">Lojista</th><th className="text-left">Email</th><th className="text-center">Plano</th><th className="text-center">Tentativas</th><th className="text-right">Falha em</th></tr></thead>
                <tbody>
                  {subHealth.past_due_users.map(u => (
                    <tr key={u.user_id} className="border-b border-slate-50">
                      <td className="py-2 font-medium">{u.name}</td>
                      <td className="text-slate-600 text-xs">{u.email}</td>
                      <td className="text-center capitalize"><Badge variant="outline">{u.plan}</Badge></td>
                      <td className="text-center font-bold text-amber-600">{u.payment_retry_count || 1}</td>
                      <td className="text-right text-xs text-slate-500">{u.payment_failed_at ? new Date(u.payment_failed_at).toLocaleDateString('pt-BR') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {subHealth.recent_events?.length > 0 && (
        <Card className="bg-white border border-slate-200" data-testid="sub-recent-events">
          <CardHeader><CardTitle className="text-lg text-slate-900 flex items-center gap-2"><Clock className="h-5 w-5" /> Eventos Recentes</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-200 text-xs text-slate-500"><th className="text-left py-2">Lojista</th><th className="text-center">Tipo</th><th className="text-center">Plano</th><th className="text-center">Status</th><th className="text-right">Valor</th><th className="text-right">Data</th></tr></thead>
                <tbody>
                  {subHealth.recent_events.map((evt, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="py-2"><p className="font-medium text-slate-900">{evt.user_name}</p><p className="text-xs text-slate-400">{evt.user_email}</p></td>
                      <td className="text-center"><Badge variant="outline" className={evt.type === 'subscription_renewal' ? 'border-blue-300 text-blue-700' : 'border-slate-300'}>{evt.type === 'subscription_renewal' ? 'Renovacao' : 'Nova'}</Badge></td>
                      <td className="text-center capitalize font-medium">{evt.plan}</td>
                      <td className="text-center"><Badge className={evt.status === 'paid' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-amber-100 text-amber-700 border-amber-200'}>{evt.status === 'paid' ? 'Pago' : 'Pendente'}</Badge></td>
                      <td className="text-right font-bold">R$ {evt.amount?.toFixed(2)}</td>
                      <td className="text-right text-xs text-slate-500">{evt.date ? new Date(evt.date).toLocaleDateString('pt-BR') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
